import React, { useState, useCallback, useEffect, memo, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/ducks';

import {
  IOrderItem,
  IDownloadTypes,
  OperationTypes,
  EnumOrderItemStatusType
} from "models/Order";
import { IBatchRequestResponse } from "models/Batch";
import { IDomainSetting } from 'models/DomainSetting';

import OrderServices from "services/order.service";
import DownloadService from "services/download.service";
import DomainSettingService from 'services/domainSetting.service';

import arrayDownload from "img/arrow-download.svg";
import imgNoteCorrection from "img/imgNoteCorrection.svg";
import logoOnlife from "img/logo-onlife.svg";
import iconBoleto from 'img/icon-boleto.svg';
import iconDownload from 'img/icon-download.svg';
import iconXml from 'img/icon-xml.svg';
import iconTracking from 'img/icon-tracking.svg';
import iconTrackingDisable from 'img/icon-tracking-disable.svg';
import iconDeliveryForecast from 'img/icon-delivery-forecast.svg';
import iconClockDeliveryForecast from 'img/icon-clock-delivery-forecast.svg';
import iconClockDeliveryForecastDisable from 'img/icon-clock-delivery-forecast-disable.svg';
import iconDevolution from 'img/menu-icon-returned.svg';
import infoImg from 'img/icon-stock-not-available.svg';
import { IsInLimitMonth } from 'utils/Date';

import {
  notaFiscalAction,
  boletoAction,
  eventName,
  notaCorrecaoAction,
  comprovanteDeEntregaAction
} from 'components/ApplicationInsights/eventNames';
import TrackItemDevolution from 'components/TrackItemDevolution';
import { Visibility } from 'components/Visibility';
import { ActionButton } from "components/ModelButton";
import Loading from 'components/Loading';
import { TrackingOrder } from './TrackingOrder';
import HideTracking from './HideTracking';
import Text from "../Text";
import { Row, Column } from "../Layout/Grid";
import NoteCorrection from 'pages/NoteCorrection';

import { IDropdownData } from "./interface";
import { TableFilterStateOrderItems } from 'components/Table/Orders/interface';

import "./styles.scss";
import { DocumentStr } from 'utils/Document';
import DevolutionTicketService from 'services/Ticket/Devolution/devolutionTicket.service';
import IDevolutionTicketForDropdown from 'models/Ticket/Devolution/IDevolutionTicketForDropdown';
import DevolutionTicketGrid from './DevolutionTicketGrid';
import DevolutionInvoicePdfDownloadButton from './DevolutionInvoicePdfDownloadButton';
import DevolutionInvoiceXmlDownloadButton from './DevolutionInvoiceXmlDownloadButton';
import { ITimeLine } from 'models/Ticket/Timeline';
import ProtocolButton from 'components/Pipefy/ProtocolNumber';
import toast from 'components/Toast/dispatch';
import CreateProtocolServices from 'services/createProtocol.service';
import { EnumUserType } from 'models/User';
import OrderItemTable from './OrderItemTable';
import { isMobileDevice } from 'utils/ScreenUtils';
import { DateOutputStr } from 'utils/DateOutput';
import { OrderItemTableContextProvider, useOrderItemTableContext } from 'contexts/OrderItemTable';
import { PhasesProtocols } from 'pages/Pipefy/enum';

const OrderDropdown: React.FC<IDropdownData> = (props) => {
  return (
    <OrderItemTableContextProvider>
      <OrderDropdownChild {...props} />
    </OrderItemTableContextProvider>
  );
};

const OrderDropdownChild: React.FC<IDropdownData> = (props) => {
  const {
    showItems,
    showOrderTrackingHistory,
    showOrderDownloadButtons,
    showOrderDownloadShippingButtons,
    showDevolutionDownloadButtons,
    id,
    timeLine,
    devolutionTimeline,
    shippingCompanyName,
    paymentMethod,
    order,
    enterpriseCode
  } = props;

  const dispatch = useDispatch();

  const user = useSelector((state: RootState) => state.user);

  const { pageSize, currentPageItem, tableItemOrderBy } = useOrderItemTableContext();

  const [ordersItem, setOrdersItem] = useState<IBatchRequestResponse<IOrderItem>>();
  const [trackingTypes, setTrackingTypes] = useState(Array<IDomainSetting>());
  const [lastItemWithDate, setLastItemWithDate] = useState<number>(0);
  const [lastItemWithDateDevolution, setLastItemWithDateDevolution] = useState<number>(0);
  const [dateDevolutionCredit, setDateDevolutionCredit] = useState(false);
  const [enterpriseBlockedDownload, setEnterpriseBlockedDownload] = useState<number[]>([]);
  const [hideTracking, setHideTracking] = useState<boolean>(false);
  const [viewTimeLine, setViewTimeLine] = useState({ timeline: false, loading: true });
  const [enterpriseCorrectionLetter, setEnterpriseCorrectionLetter] = useState<string[] | undefined>([]);
  const [devolutionTickets, setDevolutionTickets] = useState<IDevolutionTicketForDropdown[]>([]);

  const _domainSettingService = new DomainSettingService();
  const _downloadService = new DownloadService();
  const _devolutionTicketService = new DevolutionTicketService();
  const _createProtocolServices = new CreateProtocolServices();

  const filter: TableFilterStateOrderItems = {
    orderItemStatusType: order?.protheusOrderSale != null ?
      EnumOrderItemStatusType.REALIZADO :
      EnumOrderItemStatusType.PENDENTE,
  };

  const hasMoreThanOneDevolutionTicket = useMemo(() => devolutionTickets.length > 1, [devolutionTickets]);

  useEffect(() => {
    let index = 0;
    if (devolutionTickets.length !== 0) {
      const timeline = devolutionTickets[0].order.devolutionTimeline;
      const event = timeline.findLast(event => event.eventDate);
      if (event) {
        index = timeline.indexOf(event);
      }
    }
    setLastItemWithDateDevolution(index);
  }, [devolutionTickets]);

  useEffect(() => {
    _domainSettingService
      .get(undefined, undefined, undefined, {
        and: [
          { identifier: { eq: 'EnterpriseCorrectionLetter' } },
          { enabled: { eq: true } },
        ],
      })
      .then((result: IBatchRequestResponse<IDomainSetting> | undefined) => {
        if (result?.data) {
          const { value } = result?.data?.[0];
          setEnterpriseCorrectionLetter(value?.split(','));
        }
      });

    _devolutionTicketService
      .getAllForOrderDropdown(id)
      .then((tickets) => setDevolutionTickets(tickets));

    executeMountTimeline();
  }, []);

  useEffect(() => {
    // confere se o valor pedido de devolução esta na fase Compensação de Nota de Crédito
    const indexCredit = devolutionTimeline?.findIndex(x => x.description === 'Compensação de Nota de Crédito');

    if (devolutionTimeline && indexCredit) {
      if (indexCredit !== - 1 && devolutionTimeline[indexCredit].eventDate) {
        setDateDevolutionCredit(true);
      } else {
        setDateDevolutionCredit(false);
      }
    } else {
      setDateDevolutionCredit(false);
    }
  }, [dateDevolutionCredit]);

  const getOrdersItem = useCallback(() => {
    if (showItems === false) return;

    setOrdersItem(undefined);
    new OrderServices()
      .getItems(currentPageItem, pageSize, tableItemOrderBy, id, filter)
      .then((result: IBatchRequestResponse<IOrderItem> | undefined) => {
        if (result) {
          setOrdersItem(result);
        }
      });
  }, [currentPageItem, tableItemOrderBy]);

  useEffect(() => {
    getOrdersItem();
  }, [getOrdersItem]);

  const renderNoteCorrectionButton = () => {
    return IsInLimitMonth(order?.billingDate, 6);
  };

  const isCorrectionLetterEnterprise = () => {
    if (user?.userType === EnumUserType.SUPPLIER)
      return false;

    return enterpriseCorrectionLetter?.includes(order.enterpriseCode.toString()) === true;
  };

  const linkNoteCorrection = () => {
    dispatch({
      type: 'OPEN_SLIDER',
      data: {
        title: 'Solicitar correção da nota',
        content: NoteCorrection,
        data: [order]
      },
    });
  };

  const getEnterpriseBlockedDownload = async () => {
    const response = await _domainSettingService
      .get(undefined, undefined, 'sequence asc', {
        and: [
          { identifier: { eq: 'EnterpriseBlockedDownload' } },
          { enabled: { eq: true } }
        ],
      });

    const { data } = response;

    setEnterpriseBlockedDownload(JSON.parse(data[0].value));
  };

  const getShowTracking = async () => {
    const response = await _domainSettingService
      .get(undefined, undefined, 'sequence asc', {
        and: [
          { identifier: { eq: 'EnterpriseNoTracking' } },
          { enabled: { eq: true } }
        ],
      });

    const { data } = response;
    const enterprises = JSON.parse(data[0].value);

    return enterprises.includes(enterpriseCode);
  };

  const setTimelinePhases = (indexStatusDeliveryOccurrence: number, timelineParam?: ITimeLine[]) => {
    // Pega todos os itens do tipo tracking
    _domainSettingService
      .get(undefined, undefined, 'sequence asc', {
        and: [
          { identifier: { eq: 'TrackingTypes' } },
          { enabled: { eq: true } },
          { key: { ne: 'DELIVERY_OCCURRENCE' } },
        ],
      })
      .then((result: any) => {
        if (result && result.data) {
          const _trackingTypes: Array<IDomainSetting> = result.data;
          // Status que devem existir no tracking apenas se tiver ocorrência deles na timeline
          const statusHide = ['BUDGET_ENTRY', 'AWAITING_SCHEDULE', 'ON_SCHEDULE', 'IN_TRANSFER', 'DELIVERY_OCCURRENCE', 'REFUSED'];

          let statusDelete: Array<IDomainSetting> = [];
          let newArrayStatus: Array<IDomainSetting> = [];
          _trackingTypes.forEach((item) => {
            // checa se a key que esta passando esta no array que precisa haver ocorrência na historyTimeline
            const checkStatus = item.key && statusHide.includes(item.key);

            // se true, ele verifica se a key existe na timeline, se não existir ele insere no array statusDelete para ser deletada do tracking
            if (checkStatus) {
              const indexStatusHide = timelineParam?.findIndex(x => x.trackingStatus === item.key);

              if (indexStatusHide === -1) {
                statusDelete.push(item);
              }
            }

            // verifica se o item esta no array statusDelete, e retorna true se ele estiver inserido, ou false se não existir no array 
            const verifyStatus = statusDelete.includes(item);

            // se o item não existir no array statusDelete, ele verifica se existe ocorrência do item na timeline, se existir ele adiciona a data do evento e após insere no novo array de tracking completo
            if (!verifyStatus) {
              timelineParam?.map((itemTime) => {
                if (itemTime.trackingStatus === item.key) {
                  item.eventDate = itemTime.eventDate;
                }
              });
              newArrayStatus.push(item);
            }
          });

          let indexWithDate = 0;
          newArrayStatus.forEach((newItem, index) => {
            if (newItem.eventDate) {
              indexWithDate = index;
            }
          });

          let indexWithDateDevolution = 0;
          if (devolutionTimeline) {
            devolutionTimeline?.map((newItem, index) => {
              if (newItem.eventDate) {
                indexWithDateDevolution = index;
              }
            });
          }

          // Captura o index do status Entregue, se ele existir
          const indexStatusDelivered = newArrayStatus?.findIndex(item => item.key === "DELIVERED");
          // Captura o index do status Recusado Pelo Cliente, se ele existir
          const indexStatusRefused = newArrayStatus?.findIndex(item => item.key === "REFUSED");

          // se existir status de Recusado Pelo Cliente, ele remove o item Entregue
          if (indexStatusRefused !== -1) {
            newArrayStatus?.splice(indexStatusDelivered, 1);
          }

          // se existir ocorrencia na entrega ele adiciona o item ao tracking logo após o último item com data
          if (indexStatusDeliveryOccurrence !== -1 && timelineParam) {
            if (!(newArrayStatus[indexWithDate].eventDate > timelineParam[indexStatusDeliveryOccurrence].eventDate)) {
              const positionAfterLastDate = indexWithDate + 1;
              const newItem: IDomainSetting = {
                key: timelineParam[indexStatusDeliveryOccurrence].trackingStatus,
                eventDate: timelineParam[indexStatusDeliveryOccurrence].eventDate,
                description: timelineParam[indexStatusDeliveryOccurrence].description,
                value: 'OCORRÊNCIA NA ENTREGA',
                id: '5fc78a0c24572f1ec5883a45'
              };

              newArrayStatus?.splice(positionAfterLastDate, 0, newItem);
            }
          }

          // pega o index do último item com data
          setLastItemWithDate(indexWithDate);
          // pega o index do último item com data da timeline de devolução
          setLastItemWithDateDevolution(indexWithDateDevolution);
          // após todas as checagens ele insere o array completo e ajustado no state
          setTrackingTypes(newArrayStatus);
          setViewTimeLine({ timeline: true, loading: false });
        }
      });
  };

  const executeMountTimeline = async () => {
    const hideTrackingReturn = await getShowTracking();

    if (hideTrackingReturn) {
      setViewTimeLine({ timeline: true, loading: false });
      setHideTracking(true);
    }

    if (!hideTrackingReturn)
      await mountTimeline();

    await getEnterpriseBlockedDownload();
  };

  const mountTimeline = async () => {

    // Captura o index do status Aguardando Agendamento, se ele existir
    const indexStatusAwaitingSchedule = timeLine?.findIndex(item => item.trackingStatus === "AWAITING_SCHEDULE");

    // Pega o último item da timeline
    const lastItemTimeLine = timeLine ? timeLine[timeLine?.length - 1] : null;

    // se o último item do array não for Aguardando Agendamento e ele existir no array, deve ser excluí­do
    if (indexStatusAwaitingSchedule && lastItemTimeLine?.trackingStatus !== "AWAITING_SCHEDULE" && indexStatusAwaitingSchedule !== -1) {
      timeLine?.splice(indexStatusAwaitingSchedule, 1);
    }

    // verifica se existe o status Ocorrencia na Entrega na timeline
    const indexStatusDeliveryOccurrence: number = timeLine?.findIndex(item => item.trackingStatus === 'DELIVERY_OCCURRENCE') || -1;

    setTimelinePhases(indexStatusDeliveryOccurrence, timeLine);
  };

  const handleDeliveryForecastButtonClick = () => {
    if (order.status === 'ENTREGUE') {
      toast.info(
        'Previsão de entrega',
        `O pedido ${order.protheusOrderSale} foi entregue na data ${DateOutputStr(order.invoiceDeliveryDate, 'dd/MM/yyyy')}.`,
        iconDeliveryForecast,
        "Fechar"
      );
    }
    else if (order.expectedDeliveryDate === undefined) {
      if (CreateProtocolServices.isAutoAttendanceProtocolDisabled(user)) {
        CreateProtocolServices.showUnavailableDeliveryForecastInfo(order.protheusOrderSale);
        return;
      }

      toast.yesNoCustom(
        'Previsão de entrega',
        `Não foi possivel consultar a previsão de entrega do pedido ${order.protheusOrderSale}. Deseja abrir um protocolo para que nosso time de atendimento verifique a posição de entrega?`,
        () => _createProtocolServices.createProtocol(order, user),
        iconDeliveryForecast,
        undefined,
        'Sim',
        'Não'
      );
    }
  };

  const renderOrderTopMobile = () => {
    const devolution = devolutionTickets.length > 0
      ? devolutionTickets.find((ticket) => ticket.phaseStatus != PhasesProtocols.PROTOCOLO_CANCELADO)
      : undefined;

    return (
      <>

        <Row className="mt-1 mb-2 justify-between btn-action flex-md-column">
          <Row>
            <ActionButton
              img={order.status !== 'ENTREGUE' && order.expectedDeliveryDate === undefined ? iconClockDeliveryForecast : iconClockDeliveryForecastDisable}
              textButton="PREVISÃO DE ENTREGA"
              trackItem={eventName.PREVISAO_DE_ENTREGA}
              noTracking={true}
              className="ml-2 ml-sm-0 mt-md-2"
              testId="delivery-forecast-button-mobile"
              action={handleDeliveryForecastButtonClick}
            />
            <ActionButton
              img={iconBoleto}
              textButton="BOLETO"
              trackName={boletoAction.DETALHE_BOLETO}
              trackItem={eventName.BOLETO}
              noTracking={true}
              className="ml-2 ml-sm-0 mt-md-2"
              testId="billet-button-mobile"
              action={() =>
                _downloadService.OpenFileFromOrder(IDownloadTypes.BILLET, order, user?.userType)
              }
            />
            <ActionButton
              img={iconDownload}
              textButton="NOTA FISCAL"
              trackName={notaFiscalAction.DETALHE_NOTAFISCAL_PDF}
              trackItem={eventName.NOTA_FISCAL}
              noTracking={true}
              className="ml-2 ml-sm-0 mt-md-2"
              testId="invoice-button-mobile"
              action={() =>
                _downloadService.OpenFileFromOrder(IDownloadTypes.INVOICE, order)
              }
            />
            <ActionButton
              img={iconXml}
              textButton="XML NF"
              trackName={notaFiscalAction.DETALHE_NOTAFISCAL_XML}
              trackItem={eventName.NOTA_FISCAL}
              noTracking={true}
              className="ml-2 ml-sm-0 mt-md-2"
              testId="invoice-xml-button-mobile"
              action={() =>
                _downloadService.OpenFileFromOrder(IDownloadTypes.INVOICE_XML, order)
              }
            />
            <ActionButton
              img={order.status !== 'ENTREGUE' ? iconTrackingDisable : iconTracking}
              textButton="CANHOTO DE ENTREGA"
              trackName={comprovanteDeEntregaAction.PEDIDO_COMPROVANTEENTREGA}
              trackItem={eventName.COMPROVANTE_DE_ENTREGA}
              noTracking={true}
              className="ml-2 ml-sm-0 mt-md-2"
              testId="delivery-receipt-button-mobile"
              action={() => {
                if (order.status !== 'ENTREGUE') {
                  return toast.info(
                    'Pedido em processo de entrega',
                    `Este pedido tem previsão de entrega para o dia ${DateOutputStr(order.expectedDeliveryDate, 'dd/MM/yyyy')}. 
                   Para verificar o andamento da entrega, consulte o rastreio nos detalhes do pedido.`,
                    infoImg
                  );
                }
                _downloadService.OpenFileFromOrder(IDownloadTypes.RECEIPT, order);
              }}
            />
          </Row>
        </Row>
        {devolution &&
          <Row className='align-center justify-center w-100'>
            <div className='devolution-info'>
              <Row className='d-flex align-center m-2'>
                <span className='font-bold mr-2'>
                  Devolução
                </span>
                <img src={iconDevolution} alt="" />
              </Row>
              <Text size={12.5} className='text-devolution-info m-2'>
                Identificamos que existe um processo de devolução aberto registrado no protocolo {devolution.protocolNumber + ' '}
                com o motivo “<b>{devolution.ticketReason}</b>”. Para mais informações entre em contato com seu vendedor Viveo.
              </Text>
            </div>
          </Row>
        }
        {renderTracking()}

        <Text className='color-blue-gray font-bold'>Itens do pedido</Text>
      </>
    );
  };

  const renderOrderTopDefault = () => {
    return (
      <>
        <Row className={
          displayColumnMode?.clientWidth < 887 ?
            "container-info-table-order-dropdown-column" :
            "container-info-table-order-dropdown-row"}
          id="info-table-order"
        >
          <Row className="payment-breakpoint">
            <Text className="d-block font-bold color-blue-gray mt-2 mb-1" size={12}>
              {`CNPJ: ${DocumentStr({
                type: 'cnpj',
                value: order?.company?.identifier
              })}`}
            </Text>

            <Text className="d-block font-bold color-blue-gray mt-2 mb-1 px-2 d-sm-none" size={12}> | </Text>

            <Text className="d-block font-bold color-blue-gray mt-2 mb-1" size={12}>
              Filial: {order?.subsidiaryName ? `${order.subsidiaryName} - ` : ''}{order?.subsidiary}
            </Text>

            <Visibility visible={paymentMethod !== undefined}>
              <Text className="d-block font-bold color-blue-gray mt-2 mb-1 px-2 d-sm-none" size={12}> | </Text>
              <Text className="font-bold color-blue-gray mt-2 mb-1" size={12}>
                {`Condição de pagamento: ${paymentMethod}`}
              </Text>
            </Visibility>

            <Visibility visible={!!(order?.sellerName && user?.userType === "employee")}>
              <Text className="d-block font-bold color-blue-gray mt-2 mb-1 px-2 d-sm-none" size={12}> | </Text>
              <Text className="font-bold color-blue-gray mt-2 mb-1" size={12}>
                {`Vendedor: ${order?.sellerName}`}
              </Text>
            </Visibility>
          </Row>
        </Row>
      </>
    );
  };

  const renderOrderTop = () => {
    if (isMobile) {
      return renderOrderTopMobile();
    }

    return renderOrderTopDefault();
  };

  const renderTracking = () => {
    return (
      <>
        <Visibility visible={viewTimeLine.timeline}>
          <>
            {showOrderDownloadShippingButtons && (
              <Row className="mt-1 mb-2 justify-between btn-action flex-md-column">
                <Row className="flex-md-column">
                  <ActionButton
                    img={arrayDownload}
                    textButton="Download de Nota Fiscal de Remessa"
                    trackName={notaFiscalAction.DETALHE_NOTAFISCAL_REMESSA}
                    trackItem={eventName.NOTA_FISCAL_REMESSA}
                    noTracking={true}
                    action={() =>
                      _downloadService.OpenFileFromOrder(IDownloadTypes.INVOICE_SHIPPING, order)
                    }
                  />
                  <ActionButton
                    img={arrayDownload}
                    textButton=" Download do boleto"
                    trackName={boletoAction.DETALHE_BOLETO}
                    trackItem={eventName.BOLETO}
                    noTracking={true}
                    className="ml-2 ml-sm-0 mt-md-2"
                    action={() =>
                      _downloadService.OpenFileFromOrder(IDownloadTypes.BILLET, order)
                    }
                  />
                  <ActionButton
                    img={arrayDownload}
                    textButton="Download do XML de Remessa"
                    trackName={notaFiscalAction.DETALHE_NOTAFISCAL_REMESSA_XML}
                    trackItem={eventName.NOTA_FISCAL_REMESSA}
                    noTracking={true}
                    className="ml-2 ml-sm-0 mt-md-2"
                    action={() =>
                      _downloadService.OpenFileFromOrder(IDownloadTypes.INVOICE_SHIPPING_XML, order)
                    }
                  />
                </Row>
              </Row>
            )}
            <Visibility visible={hideTracking === false}>
              <TrackingOrder
                shippingCompanyName={shippingCompanyName}
                trackingTypes={trackingTypes}
                lastItemWithDate={lastItemWithDate}
                expectedDeliveryDate={order.expectedDeliveryDate}
                isGreenDelivery={isGreenDelivery}
              />
            </Visibility>

            <Visibility visible={hideTracking === true}>
              <HideTracking order={order} />
            </Visibility>
          </>
        </Visibility>

        <Visibility visible={!viewTimeLine.timeline}>
          <Row
            className="bg-gray-20 py-2 d-flex justify-center align-items"
            style={{
              borderRadius: '0 0 10px 10px',
              height: 40,
              maxHeight: 40,
            }}
          >
            <Text
              size={12}
              className="d-flex align-center justify-center color-primary font-bold"
            >
              Item sem histórico
            </Text>
          </Row>
        </Visibility>
      </>
    );
  };

  const classNoteCorrection = `ml-2 ml-sm-0 mt-md-2 ${renderNoteCorrectionButton() ?
    "" : "disable-color"}`;

  const isGreenDelivery: boolean = (order?.carbonEmission && order?.carbonEmission.length > 0) ?
    order?.carbonEmission !== 'Emite' : false;

  const displayColumnMode = document.getElementById('info-table-order') as HTMLInputElement;

  const isMobile = isMobileDevice();

  return (
    <Column className="animated fadeIn px-md-2 px-3 bg-white overflow-x-auto">
      {(showItems || showItems === undefined) && (
        <>
          {renderOrderTop()}
          {ordersItem && (
            <OrderItemTable
              id={id}
              enterpriseCode={order?.enterpriseCode}
              enterpriseBlockedDownload={enterpriseBlockedDownload}
              ordersItem={ordersItem}
              onReload={getOrdersItem}
            />
          )}
        </>
      )}

      {(showOrderTrackingHistory || showOrderTrackingHistory === undefined) &&
        props != null &&
        (viewTimeLine.loading ? (
          <Row className="py-2 d-flex justify-center align-items">
            <Loading size={30} onlySpinner />
          </Row>
        ) :
          <>
            <Visibility visible={false}>
              {(showOrderDownloadButtons !== false && !isMobile) && (
                <Row className="mt-1 mb-2 justify-between btn-action flex-md-column">
                  <Row className="flex-md-column">
                    <ActionButton
                      img={arrayDownload}
                      textButton="Download da Nota Fiscal"
                      trackName={notaFiscalAction.DETALHE_NOTAFISCAL_PDF}
                      trackItem={eventName.NOTA_FISCAL}
                      noTracking={true}
                      action={() =>
                        _downloadService.OpenFileFromOrder(IDownloadTypes.INVOICE, order)
                      }
                    />
                    <ActionButton
                      img={arrayDownload}
                      textButton=" Download do boleto"
                      trackName={boletoAction.DETALHE_BOLETO}
                      trackItem={eventName.BOLETO}
                      noTracking={true}
                      className="ml-2 ml-sm-0 mt-md-2"
                      action={() =>
                        _downloadService.OpenFileFromOrder(IDownloadTypes.BILLET, order, user?.userType)
                      }
                    />
                    <ActionButton
                      img={arrayDownload}
                      textButton="Download do XML"
                      trackName={notaFiscalAction.DETALHE_NOTAFISCAL_XML}
                      trackItem={eventName.NOTA_FISCAL}
                      noTracking={true}
                      className="ml-2 ml-sm-0 mt-md-2"
                      action={() =>
                        _downloadService.OpenFileFromOrder(IDownloadTypes.INVOICE_XML, order)
                      }
                    />
                    {order && order.operationType === OperationTypes.VENDA_ORDEM && (
                      <ActionButton
                        img={arrayDownload}
                        textButton="Download de Nota Fiscal de Remessa"
                        trackName={notaFiscalAction.NOTA_REMESSA}
                        trackItem={eventName.NOTA_FISCAL_REMESSA}
                        noTracking={true}
                        className="ml-2"
                        action={() =>
                          _downloadService.OpenFileFromOrder(IDownloadTypes.INVOICE_SHIPPING, order)
                        }
                      />
                    )}
                  </Row>
                  <Visibility visible={isCorrectionLetterEnterprise()}>
                    <Row className="flex-md-column">
                      <ActionButton
                        img={imgNoteCorrection}
                        textButton="Solicitar correção da nota"
                        trackName={notaCorrecaoAction.NOTA_CORRECAO}
                        trackItem={eventName.NOTA_DE_CORRECAO}
                        noTracking={true}
                        tooltip={!renderNoteCorrectionButton() && "Pedido fora da validade para solicitar carta de correção"}
                        toolTipPlace={"left"}
                        action={() => renderNoteCorrectionButton() && linkNoteCorrection()}
                        className={classNoteCorrection}
                      />
                    </Row>
                  </Visibility>
                </Row>
              )}
            </Visibility>

            <Visibility visible={!isMobile}>
              {renderTracking()}
            </Visibility>
          </>
        )}

      {!isMobile && devolutionTickets.length > 0 &&
        <>
          <Column className="mb-1">
            <Row>
              <Text className="font-bold color-blue-gray mt-2 mb-1" size={12}>Devolução</Text>
              <Text className="font-bold color-blue-gray mt-2 mb-1 px-2 d-sm-none" size={12}> | </Text>
              <Text className="color-blue-gray mt-2 mb-1" size={12}>Protocolo:</Text>
              <ProtocolButton data={devolutionTickets[0]} />
            </Row>
            <Row
              className="tracking-item mt-3 mb-1 pb-2 mx-auto bg-white w-100 px-3 align-center align-start overflow-x-auto"
              style={{ borderRadius: '10px 10px 0px 0px' }}
            >
              {devolutionTickets[0].order.devolutionTimeline.map((item, index) => (
                <>
                  <TrackItemDevolution
                    key={index}
                    item={item}
                    indexCurrentItem={index}
                    lastItemWithDate={lastItemWithDateDevolution}
                  />
                  {index !== devolutionTickets[0].order.devolutionTimeline.length - 1 && <Column className={"icon-track-item"} />}
                </>
              ))
              }
            </Row>
          </Column>
          {showDevolutionDownloadButtons && devolutionTickets[0].order.isInvoiceDownloadEnabled && (
            <Row className="mt-1 mb-2">
              <DevolutionInvoicePdfDownloadButton order={order} />
              <DevolutionInvoiceXmlDownloadButton order={order} className="ml-2" />
            </Row>
          )}

          {hasMoreThanOneDevolutionTicket && (
            <DevolutionTicketGrid
              data={devolutionTickets.slice(1)}
              showDevolutionDownloadButtons={showDevolutionDownloadButtons} />
          )}
        </>
      }

    </Column>
  );
};

export default memo(OrderDropdown);